import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore, isAuthenticated, hasRole } from '@/stores/auth.store';
import { PageSpinner } from '@/components/ui';
import { UserRole } from '@/types/models';
import { useContext } from '@/hooks/useContext';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requireRoles?: UserRole[];
  redirectTo?: string;
  requiresDoctor?: boolean;
  requiresClinic?: boolean;
}

export function ProtectedRoute({
  children,
  requireRoles,
  redirectTo = '/login',
  requiresDoctor = false,
  requiresClinic = false
}: ProtectedRouteProps) {
  const location = useLocation();
  const authState = useAuthStore();
  const { isInitialized, mustChangePassword } = authState;
  const { clinic, doctor } = useContext();

  if (!isInitialized) {
    return <PageSpinner label="Verificando autenticação..." />;
  }

  if (!isAuthenticated(authState)) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (mustChangePassword && location.pathname !== '/definir-senha') {
    return <Navigate to="/definir-senha" replace />;
  }

  if (requireRoles && requireRoles.length > 0) {
    const hasRequiredRole = requireRoles.some(role => hasRole(authState, role));
    if (!hasRequiredRole) {
      return <Navigate to="/acesso-negado" replace />;
    }
  }

  // If doctor is required but not selected, redirect to doctor selection
  if (requiresDoctor && !doctor) {
    // If clinic is also not selected, go to clinic selection first
    if (!clinic) {
      return <Navigate to="/clinicas" state={{ from: location }} replace />;
    }
    return <Navigate to="/medicos" state={{ from: location }} replace />;
  }

  // If only clinic is required but not selected, redirect to clinic selection
  if (requiresClinic && !clinic) {
    return <Navigate to="/clinicas" state={{ from: location }} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

export function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute requireRoles={['Administrador']}>
      {children}
    </ProtectedRoute>
  );
}